import { createLeaderFollowerStore } from 'collaboration-sidebar-common/store/leaderFollowerStore';
import { COMMENT_FILTER_VALUES } from '../constants/commentFilterConstants';
import { getCommentIdParam } from '../utils/urls';
import { magpieStrategy } from 'collaboration-sidebar-common/store/magpieStrategy';
import { CollaborationSidebarAudience, CollaborationSidebarSessionId } from 'collaboration-sidebar-common/utils/magpie';
import * as t from 'collaboration-sidebar-common/utils/schema';
import produce from 'immer';
import { PLACEMENTS } from 'foundations-components/transitional/components/Tooltip';
const makeImmerSet = setter => {
  return updater => setter(state => produce(state, updater));
};
export const commentSchema = t.and(t.object({
  id: t.string,
  createdAt: t.string,
  updatedAt: t.string,
  type: t.union(t.literal('COMMENT'), t.literal('REPLY'), t.literal('DEEP_COMMENT')),
  archived: t.boolean,
  atMentionedUserIds: t.array(t.number),
  message: t.string,
  resolved: t.boolean,
  userId: t.number,
  read: t.boolean,
  parentId: t.nullable(t.string),
  archivedAt: t.nullable(t.string)
}), t.partialObject({
  commentLocation: t.nullable(t.string),
  parentCommentId: t.nullable(t.number),
  metaDescription: t.nullable(t.and(t.object({
    translationKey: t.string,
    locationPath: t.string
  }), t.partialObject({
    options: t.any
  }))),
  attachmentIds: t.array(t.string)
}));
export const replyCommentSchema = t.and(commentSchema, t.object({
  parentId: t.string
}));
export const tabSchema = t.union(t.literal('comments'), t.literal('tasks'), t.literal('calendar'), t.literal('approvals'), t.literal('copilot'), t.literal('activityFeed'));
export const featuresSchema = t.array(t.union(tabSchema, t.literal('deepCommenting')));
export const selectedAssetObjectSchema = t.object({
  objectId: t.union(t.string, t.number),
  objectType: t.string
});
export const taskStatusSchema = t.union(t.literal('ALL'), t.literal('DUE_TODAY'), t.literal('OVERDUE'), t.literal('ALL_COMPLETED'));
export const tasksFiltersSchema = t.partialObject({
  initialCampaignGuid: t.string,
  campaignEditable: t.boolean,
  initialAssigneeIsCurrentUser: t.boolean,
  marketingStudioCardId: t.string,
  initialTaskStatus: taskStatusSchema
});
export const tasksFormSchema = t.partialObject({
  showTimeField: t.boolean,
  initialCampaignGuid: t.string,
  campaignEditable: t.boolean,
  initialAssigneeIsCurrentUser: t.boolean,
  marketingStudioCardId: t.string
});
export const tasksConfigSchema = t.partialObject({
  lockToCampaign: t.string,
  filters: tasksFiltersSchema,
  form: tasksFormSchema
});
export const commentsFilterSchema = t.union(t.literal('open'), t.literal('all'), t.literal('resolved'), t.literal('unread'));
export const replyCountSchema = t.object({
  replyCount: t.number
});
export const embeddedCommentCoordinatesSchema = t.object({
  top: t.number,
  left: t.number,
  width: t.number,
  height: t.number
});
export const embeddedMetaDescriptionSchema = t.and(t.object({
  translationKey: t.string
}), t.partialObject({
  options: t.any
}));
export const commentableAreaClickCoordinatesSchema = t.object({
  x: t.number,
  y: t.number
});
export const deleteCommentRequestSchema = t.object({
  loading: t.boolean,
  error: t.boolean
});
export const modulesWithRepliesSchema = t.record(t.string, commentSchema);
export const embeddedPopoverPlacementSchema = t.union(...PLACEMENTS.map(placement => t.literal(placement)));
export const collaborationSidebarStateSchema = t.and(t.object({
  isOpen: t.boolean,
  tab: tabSchema,
  SELECTED_ASSET: t.nullable(selectedAssetObjectSchema),
  commentThread: t.union(t.string, t.literal(null)),
  hideEmbeddedComments: t.boolean,
  appName: t.union(t.string, t.literal(null)),
  objectId: t.union(t.string, t.number, t.literal(null)),
  objectType: t.union(t.string, t.literal(null)),
  ctaUrl: t.union(t.string, t.literal(null)),
  modulesWithReplies: modulesWithRepliesSchema,
  replyCounts: t.record(t.string, replyCountSchema),
  isEmbeddedCommentingEnabled: t.boolean,
  isCommentModeActive: t.boolean,
  broadcastCommentMessage: t.any,
  commentsFilter: commentsFilterSchema,
  assetsLastUpdated: t.record(t.string, t.nullable(t.string)),
  notificationSettingsOpen: t.boolean,
  unreadRepliesMap: t.record(t.string, t.array(commentSchema)),
  isEmbeddedPopoverOpen: t.boolean,
  embeddedCommentCoordinates: embeddedCommentCoordinatesSchema,
  isEmbeddedOpenFromCommentMode: t.boolean,
  deleteCommentRequest: deleteCommentRequestSchema
}), t.partialObject({
  enabledFeatures: t.nullable(featuresSchema),
  embeddedPopoverPlacement: embeddedPopoverPlacementSchema,
  embeddedMetaDescription: embeddedMetaDescriptionSchema,
  embeddedModuleID: t.string,
  embeddedPopoverAnchorSelector: t.string,
  tasksConfig: tasksConfigSchema,
  commentableAreaClickCoordinates: commentableAreaClickCoordinatesSchema,
  ctaCommentId: t.nullable(t.string),
  // unused but included for backwards compatibility
  hasUnrecoverableError: t.boolean
}));
export const createTasksConfig = ({
  baseConfig,
  overrides = {}
} = {}) => {
  var _overrides$lockToCamp, _overrides$filters$in, _overrides$filters, _baseConfig$filters, _overrides$filters$ca, _overrides$filters2, _baseConfig$filters2, _overrides$filters$in2, _overrides$filters3, _baseConfig$filters3, _overrides$filters$in3, _overrides$filters4, _baseConfig$filters4, _overrides$filters$ma, _overrides$filters5, _baseConfig$filters5, _overrides$form$showT, _overrides$form, _baseConfig$form, _overrides$form$initi, _overrides$form2, _baseConfig$form2, _overrides$form$campa, _overrides$form3, _baseConfig$form3, _overrides$form$initi2, _overrides$form4, _baseConfig$form4, _overrides$form$marke, _overrides$form5, _baseConfig$form5;
  return {
    lockToCampaign: (_overrides$lockToCamp = overrides === null || overrides === void 0 ? void 0 : overrides.lockToCampaign) !== null && _overrides$lockToCamp !== void 0 ? _overrides$lockToCamp : baseConfig === null || baseConfig === void 0 ? void 0 : baseConfig.lockToCampaign,
    filters: {
      initialCampaignGuid: (_overrides$filters$in = overrides === null || overrides === void 0 || (_overrides$filters = overrides.filters) === null || _overrides$filters === void 0 ? void 0 : _overrides$filters.initialCampaignGuid) !== null && _overrides$filters$in !== void 0 ? _overrides$filters$in : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters = baseConfig.filters) === null || _baseConfig$filters === void 0 ? void 0 : _baseConfig$filters.initialCampaignGuid,
      campaignEditable: (_overrides$filters$ca = overrides === null || overrides === void 0 || (_overrides$filters2 = overrides.filters) === null || _overrides$filters2 === void 0 ? void 0 : _overrides$filters2.campaignEditable) !== null && _overrides$filters$ca !== void 0 ? _overrides$filters$ca : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters2 = baseConfig.filters) === null || _baseConfig$filters2 === void 0 ? void 0 : _baseConfig$filters2.campaignEditable,
      initialAssigneeIsCurrentUser: (_overrides$filters$in2 = overrides === null || overrides === void 0 || (_overrides$filters3 = overrides.filters) === null || _overrides$filters3 === void 0 ? void 0 : _overrides$filters3.initialAssigneeIsCurrentUser) !== null && _overrides$filters$in2 !== void 0 ? _overrides$filters$in2 : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters3 = baseConfig.filters) === null || _baseConfig$filters3 === void 0 ? void 0 : _baseConfig$filters3.initialAssigneeIsCurrentUser,
      initialTaskStatus: (_overrides$filters$in3 = overrides === null || overrides === void 0 || (_overrides$filters4 = overrides.filters) === null || _overrides$filters4 === void 0 ? void 0 : _overrides$filters4.initialTaskStatus) !== null && _overrides$filters$in3 !== void 0 ? _overrides$filters$in3 : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters4 = baseConfig.filters) === null || _baseConfig$filters4 === void 0 ? void 0 : _baseConfig$filters4.initialTaskStatus,
      marketingStudioCardId: (_overrides$filters$ma = overrides === null || overrides === void 0 || (_overrides$filters5 = overrides.filters) === null || _overrides$filters5 === void 0 ? void 0 : _overrides$filters5.marketingStudioCardId) !== null && _overrides$filters$ma !== void 0 ? _overrides$filters$ma : baseConfig === null || baseConfig === void 0 || (_baseConfig$filters5 = baseConfig.filters) === null || _baseConfig$filters5 === void 0 ? void 0 : _baseConfig$filters5.marketingStudioCardId
    },
    form: {
      showTimeField: (_overrides$form$showT = overrides === null || overrides === void 0 || (_overrides$form = overrides.form) === null || _overrides$form === void 0 ? void 0 : _overrides$form.showTimeField) !== null && _overrides$form$showT !== void 0 ? _overrides$form$showT : baseConfig === null || baseConfig === void 0 || (_baseConfig$form = baseConfig.form) === null || _baseConfig$form === void 0 ? void 0 : _baseConfig$form.showTimeField,
      initialCampaignGuid: (_overrides$form$initi = overrides === null || overrides === void 0 || (_overrides$form2 = overrides.form) === null || _overrides$form2 === void 0 ? void 0 : _overrides$form2.initialCampaignGuid) !== null && _overrides$form$initi !== void 0 ? _overrides$form$initi : baseConfig === null || baseConfig === void 0 || (_baseConfig$form2 = baseConfig.form) === null || _baseConfig$form2 === void 0 ? void 0 : _baseConfig$form2.initialCampaignGuid,
      campaignEditable: (_overrides$form$campa = overrides === null || overrides === void 0 || (_overrides$form3 = overrides.form) === null || _overrides$form3 === void 0 ? void 0 : _overrides$form3.campaignEditable) !== null && _overrides$form$campa !== void 0 ? _overrides$form$campa : baseConfig === null || baseConfig === void 0 || (_baseConfig$form3 = baseConfig.form) === null || _baseConfig$form3 === void 0 ? void 0 : _baseConfig$form3.campaignEditable,
      initialAssigneeIsCurrentUser: (_overrides$form$initi2 = overrides === null || overrides === void 0 || (_overrides$form4 = overrides.form) === null || _overrides$form4 === void 0 ? void 0 : _overrides$form4.initialAssigneeIsCurrentUser) !== null && _overrides$form$initi2 !== void 0 ? _overrides$form$initi2 : baseConfig === null || baseConfig === void 0 || (_baseConfig$form4 = baseConfig.form) === null || _baseConfig$form4 === void 0 ? void 0 : _baseConfig$form4.initialAssigneeIsCurrentUser,
      marketingStudioCardId: (_overrides$form$marke = overrides === null || overrides === void 0 || (_overrides$form5 = overrides.form) === null || _overrides$form5 === void 0 ? void 0 : _overrides$form5.marketingStudioCardId) !== null && _overrides$form$marke !== void 0 ? _overrides$form$marke : baseConfig === null || baseConfig === void 0 || (_baseConfig$form5 = baseConfig.form) === null || _baseConfig$form5 === void 0 ? void 0 : _baseConfig$form5.marketingStudioCardId
    }
  };
};
export const createCollaborationSidebarState = overrides => {
  var _overrides$modulesWit, _overrides$unreadRepl;
  return Object.assign({
    isOpen: false,
    tab: 'comments',
    SELECTED_ASSET: null,
    commentThread: null,
    hideEmbeddedComments: false,
    enabledFeatures: [],
    appName: null,
    objectId: null,
    objectType: null,
    ctaUrl: null,
    replyCounts: {},
    isEmbeddedCommentingEnabled: false,
    isCommentModeActive: false,
    broadcastCommentMessage: null,
    commentsFilter: COMMENT_FILTER_VALUES.open,
    ctaCommentId: getCommentIdParam(),
    assetsLastUpdated: {},
    notificationSettingsOpen: false,
    isEmbeddedPopoverOpen: false,
    embeddedPopoverPlacement: 'top',
    embeddedCommentCoordinates: {
      top: 0,
      left: 0,
      width: 0,
      height: 0
    },
    embeddedModuleID: undefined,
    isEmbeddedOpenFromCommentMode: false,
    embeddedPopoverAnchorSelector: undefined,
    commentableAreaClickCoordinates: undefined,
    deleteCommentRequest: {
      loading: false,
      error: false
    }
  }, overrides, {
    modulesWithReplies: (_overrides$modulesWit = overrides === null || overrides === void 0 ? void 0 : overrides.modulesWithReplies) !== null && _overrides$modulesWit !== void 0 ? _overrides$modulesWit : {},
    unreadRepliesMap: (_overrides$unreadRepl = overrides === null || overrides === void 0 ? void 0 : overrides.unreadRepliesMap) !== null && _overrides$unreadRepl !== void 0 ? _overrides$unreadRepl : {},
    tasksConfig: createTasksConfig({
      overrides: overrides === null || overrides === void 0 ? void 0 : overrides.tasksConfig
    })
  });
};
const setIfDefined = (state, key, value) => {
  if (value === undefined) {
    return;
  }
  state[key] = value;
};
export const {
  makeLeader: makeCollaborationSidebarStateLeader,
  makeFollower: makeCollaborationSidebarStateFollower
} = createLeaderFollowerStore({
  syncStrategyFactory: magpieStrategy({
    key: `collaboration-sidebar-state-${CollaborationSidebarSessionId}`,
    audience: CollaborationSidebarAudience,
    valueSchema: collaborationSidebarStateSchema
  }),
  key: `collaboration-sidebar-state-${CollaborationSidebarSessionId}`,
  actions: set => {
    const immerSet = makeImmerSet(set);
    return {
      openSidebar: tab => immerSet(draft => {
        draft.isOpen = true;
        draft.tab = tab !== null && tab !== void 0 ? tab : 'comments';
      }),
      openComments: ({
        isCommentModeActive,
        commentThread,
        ctaCommentId,
        SELECTED_ASSET,
        commentsFilter
      } = {}) => immerSet(draft => {
        draft.isOpen = true;
        draft.tab = 'comments';
        setIfDefined(draft, 'isCommentModeActive', isCommentModeActive);
        setIfDefined(draft, 'commentsFilter', commentsFilter);
        setIfDefined(draft, 'commentThread', commentThread);
        setIfDefined(draft, 'ctaCommentId', ctaCommentId);
        setIfDefined(draft, 'SELECTED_ASSET', SELECTED_ASSET);
      }),
      openTasks: () => set({
        isOpen: true,
        tab: 'tasks',
        isCommentModeActive: false
      }),
      openCalendar: () => set({
        isOpen: true,
        tab: 'calendar',
        isCommentModeActive: false
      }),
      openApprovals: () => set({
        isOpen: true,
        tab: 'approvals',
        isCommentModeActive: false
      }),
      openCopilot: () => set({
        isOpen: true,
        tab: 'copilot',
        isCommentModeActive: false
      }),
      openActivityFeed: () => set({
        isOpen: true,
        tab: 'activityFeed',
        isCommentModeActive: false
      }),
      closeSidebar: ({
        disableCommentMode
      } = {}) => immerSet(draft => {
        draft.isOpen = false;
        if (disableCommentMode !== undefined) {
          draft.isCommentModeActive = !disableCommentMode;
        }
      }),
      onUpdateCollaborationSidebarState: updates => set(state => Object.assign({}, state, updates)),
      persistPropsToCollaborationSidebarState: ({
        isEmbeddedCommentingEnabled,
        appName,
        objectId,
        objectType,
        ctaUrl,
        enabledFeatures,
        tasksConfig
      }) => immerSet(draft => {
        setIfDefined(draft, 'isEmbeddedCommentingEnabled', isEmbeddedCommentingEnabled);
        setIfDefined(draft, 'appName', appName);
        setIfDefined(draft, 'objectId', objectId);
        setIfDefined(draft, 'objectType', objectType);
        setIfDefined(draft, 'ctaUrl', ctaUrl);
        setIfDefined(draft, 'enabledFeatures', enabledFeatures);
        draft.tasksConfig = createTasksConfig({
          baseConfig: draft.tasksConfig,
          overrides: tasksConfig
        });
      }),
      enableCommentMode: () => set({
        isOpen: false,
        isCommentModeActive: true
      }),
      disableCommentMode: () => set({
        isCommentModeActive: false
      }),
      selectAsset: asset => set({
        SELECTED_ASSET: asset
      }),
      clearSelectedAsset: () => set({
        SELECTED_ASSET: null
      })
    };
  }
});